import React, { useEffect } from 'react';
import {
  Alert,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { goBackOrHome } from '../../navigation/goBackOrHome';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { MaterialIcons } from '@expo/vector-icons';
import { useAppDispatch, useAppSelector } from '../../store/store';
import {
  updateProfile,
  clearError,
  selectUser,
  selectIsUpdatingProfile,
  selectAuthError,
  selectValidationErrors,
} from './authSlice';
import {
  BrandLogo,
  IconTextField,
  PrimaryCTA,
} from '../../components';
import { fpColors } from '../../styles/theme';
import type { UpdateProfileRequest } from '../../api/types';

const schema = yup.object({
  full_name: yup
    .string()
    .min(2, 'Name must be at least 2 characters')
    .required('Full name is required'),
});

/**
 * Edit profile — Fidelity Purple. Custom top bar (back + brand + save),
 * single IconTextField, error banner. Native stack header disabled.
 */
export const EditProfileScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();

  const user = useAppSelector(selectUser);
  const isLoading = useAppSelector(selectIsUpdatingProfile);
  const error = useAppSelector(selectAuthError);
  const validationErrors = useAppSelector(selectValidationErrors);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<UpdateProfileRequest>({
    resolver: yupResolver(schema),
    defaultValues: { full_name: user?.full_name || '' },
  });

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const onSubmit = async (data: UpdateProfileRequest) => {
    const result = await dispatch(updateProfile(data));
    if (updateProfile.fulfilled.match(result)) goBackOrHome(navigation);
  };

  const handleBack = () => {
    if (isDirty) {
      Alert.alert(
        'Unsaved changes',
        'You have unsaved changes. Discard them?',
        [
          { text: 'Keep editing', style: 'cancel' },
          {
            text: 'Discard',
            style: 'destructive',
            onPress: () => goBackOrHome(navigation),
          },
        ],
      );
    } else {
      goBackOrHome(navigation);
    }
  };

  const bannerError =
    (validationErrors && typeof validationErrors === 'string'
      ? validationErrors
      : null) ||
    (typeof error === 'string' ? error : null);

  return (
    <View style={styles.page}>
      <View style={styles.topBar}>
        <View style={styles.topBarInner}>
          <Pressable
            onPress={handleBack}
            hitSlop={8}
            style={styles.topBtn}
            accessibilityRole="button"
            accessibilityLabel="Back"
          >
            <MaterialIcons name="arrow-back" size={22} color={fpColors.onSurface} />
          </Pressable>
          <View style={styles.topBrand}>
            <BrandLogo variant="horizontal" height={22} tone="color" />
          </View>
          <View style={styles.topBtn} />
        </View>
      </View>

      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={styles.scroll}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <Text style={styles.kicker}>PROFILE</Text>
          <Text style={styles.h1}>Edit profile</Text>
          <Text style={styles.subtitle}>Update your display name.</Text>

          <View style={styles.card}>
            <Controller
              control={control}
              name="full_name"
              render={({ field: { onChange, onBlur, value } }) => (
                <IconTextField
                  label="Full name"
                  leftIcon="person-outline"
                  placeholder="Enter your full name"
                  value={value || ''}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  error={errors.full_name?.message}
                  autoCapitalize="words"
                  autoComplete="name"
                  autoFocus
                />
              )}
            />
          </View>

          {bannerError ? (
            <View style={styles.errorBanner}>
              <MaterialIcons
                name="error-outline"
                size={16}
                color={fpColors.error}
              />
              <Text style={styles.errorText}>{bannerError}</Text>
            </View>
          ) : null}

          <PrimaryCTA
            label={isLoading ? 'Saving…' : 'Save changes'}
            onPress={handleSubmit(onSubmit)}
            loading={isLoading}
            disabled={!isDirty}
            iconRight={isLoading ? null : 'check-circle'}
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
  },
  topBarInner: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    gap: 10,
  },
  topBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  topBrand: { flex: 1, alignItems: 'center' },

  scroll: { padding: 20, gap: 14, paddingBottom: 30 },
  kicker: {
    fontFamily: 'Inter_500Medium',
    fontSize: 11,
    color: fpColors.primary,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
  },
  h1: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 26,
    color: fpColors.onSurface,
    letterSpacing: -0.4,
    marginTop: 2,
  },
  subtitle: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    marginBottom: 6,
  },

  card: {
    padding: 14,
    borderRadius: 16,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },

  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 10,
    backgroundColor: fpColors.errorContainer,
  },
  errorText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onErrorContainer,
  },
});
